import React from 'react';
import { Modal, Form, Input, InputNumber, Select, Row, Col, message } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { apiService } from '@/services/api.service';
import { API_ENDPOINTS } from '@/constants/api.constants';
import type { Category } from '@/types/entities';
import { logger } from '@/utils/logger';

const { TextArea } = Input;
const { Option } = Select;

interface EditCategoryModalProps {
  visible: boolean;
  category: Category | null;
  onCancel: () => void;
  onSuccess: () => void;
}

interface CategoryFormValues {
  name: string;
  description?: string;
  weightFrom: number;
  weightTo: number;
  ageFrom: number;
  ageTo: number;
  gender: 'male' | 'female' | 'mixed';
  beltLevels: string[];
}

const BELT_LEVELS = [
  'белый',
  'желтый',
  'оранжевый',
  'зеленый',
  'синий',
  'коричневый',
  'черный'
];

const EditCategoryModal: React.FC<EditCategoryModalProps> = ({
  visible,
  category,
  onCancel,
  onSuccess
}) => {
  const [form] = useForm<CategoryFormValues>();
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (category && visible) {
      form.setFieldsValue({
        name: category.name,
        description: category.description || undefined,
        weightFrom: category.weightFrom,
        weightTo: category.weightTo,
        ageFrom: category.ageFrom,
        ageTo: category.ageTo,
        gender: category.gender,
        beltLevels: category.beltLevels
      });
    }
  }, [category, visible, form]);

  const handleSubmit = async () => {
    try {
      if (!category) return;

      const values = await form.validateFields();
      setLoading(true);

      await apiService.put(
        `${API_ENDPOINTS.TOURNAMENTS.CATEGORIES}/${category.id}`,
        values
      );

      message.success('Категория успешно обновлена');
      form.resetFields();
      onSuccess();
    } catch (error) {
      logger.error('Error updating category:', error);
      message.error('Ошибка при обновлении категории');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title="Редактировать категорию"
      open={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          gender: 'male',
          beltLevels: []
        }}
      >
        <Form.Item
          name="name"
          label="Название"
          rules={[{ required: true, message: 'Введите название категории' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="description"
          label="Описание"
        >
          <TextArea />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="weightFrom"
              label="Вес от (кг)"
              rules={[{ required: true, message: 'Укажите минимальный вес' }]}
            >
              <InputNumber min={0} max={999} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="weightTo"
              label="Вес до (кг)"
              rules={[{ required: true, message: 'Укажите максимальный вес' }]}
            >
              <InputNumber min={0} max={999} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="ageFrom"
              label="Возраст от"
              rules={[{ required: true, message: 'Укажите минимальный возраст' }]}
            >
              <InputNumber min={0} max={100} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="ageTo"
              label="Возраст до"
              rules={[{ required: true, message: 'Укажите максимальный возраст' }]}
            >
              <InputNumber min={0} max={100} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="gender"
          label="Пол"
          rules={[{ required: true, message: 'Выберите пол' }]}
        >
          <Select>
            <Option value="male">Мужской</Option>
            <Option value="female">Женский</Option>
            <Option value="mixed">Смешанный</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="beltLevels"
          label="Уровни поясов"
          rules={[{ required: true, message: 'Выберите хотя бы один уровень пояса' }]}
        >
          <Select mode="multiple" placeholder="Выберите уровни поясов">
            {BELT_LEVELS.map(belt => (
              <Option key={belt} value={belt}>
                {belt}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditCategoryModal;
